import { ArrowRight, BriefcaseBusiness, CircleGauge, Layers3, PencilRuler, SearchCheck, Sparkles, Target, Workflow } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TRUEVANCE_BRAND_NAME, TruevanceBrandHeader, useTruevanceBranding } from "./TruevanceBranding";
import {
  assessmentFramework,
  consultingPipelineStages,
  engagementMetrics,
  formatCurrency,
  formatPercent,
  initializeTruevanceStore,
  reusablePatterns,
  serviceCategories,
  truevanceOperationalLinks,
  truevanceWorkspaceLinks,
  type TruevanceWorkspaceKey,
} from "./TruevanceConnectData";
import { calculateTruevanceMetrics, loadTruevanceWorkspace } from "./TruevanceConnectApi";

const workspaceContent: Record<
  TruevanceWorkspaceKey,
  {
    title: string;
    eyebrow: string;
    description: string;
    emphasis: string;
  }
> = {
  hub: {
    title: TRUEVANCE_BRAND_NAME,
    eyebrow: TRUEVANCE_BRAND_NAME,
    description:
      "A reusable operations layer for consulting, assessment, implementation, optimization, ongoing support, and persistent business records.",
    emphasis: "Business operating platform",
  },
  "consulting-pipeline": {
    title: "Consulting Pipeline",
    eyebrow: TRUEVANCE_BRAND_NAME,
    description:
      "Track discovery consultation through ongoing support using the same status engine pattern that powers the Business Center OS journey.",
    emphasis: "Pipeline workspace",
  },
  "assessment-framework": {
    title: "Assessment Framework",
    eyebrow: TRUEVANCE_BRAND_NAME,
    description:
      "Capture business challenges, opportunities, processes, systems, bottlenecks, recommendations, and suggested solutions.",
    emphasis: "Assessment workspace",
  },
  "client-engagement-dashboard": {
    title: "Client Engagement Dashboard",
    eyebrow: TRUEVANCE_BRAND_NAME,
    description:
      "View active consulting clients, assessments in progress, strategy work, implementations, follow-ups, and opportunities.",
    emphasis: "Engagement dashboard",
  },
  "service-categories": {
    title: "Service Categories",
    eyebrow: TRUEVANCE_BRAND_NAME,
    description:
      "Organize consulting, digital solutions, website, automation, dashboard, and business systems projects into reusable categories.",
    emphasis: "Service catalog",
  },
  "platform-architecture": {
    title: "Platform Architecture",
    eyebrow: TRUEVANCE_BRAND_NAME,
    description:
      "Document the shared workflow, dashboard, record lifecycle, and empty-state patterns that can later become ClientFlow Studio.",
    emphasis: "Shared architecture",
  },
};

export function TruevanceConnectPage({ workspace }: { workspace: TruevanceWorkspaceKey }) {
  const navigate = useNavigate();
  const location = useLocation();
  const details = workspaceContent[workspace];
  const [workspaceData, setWorkspaceData] = useState<Awaited<ReturnType<typeof loadTruevanceWorkspace>> | null>(null);

  useTruevanceBranding(details.title, details.description);

  useEffect(() => {
    if (!location.hash) return;
    const target = document.getElementById(location.hash.slice(1));
    target?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [location.hash]);

  useEffect(() => {
    let cancelled = false;
    loadTruevanceWorkspace()
      .then((data) => {
        if (!cancelled) setWorkspaceData(data);
      })
          .catch(() => {
        if (!cancelled) {
          setWorkspaceData({
            store: import.meta.env.DEV
              ? initializeTruevanceStore()
              : { opportunities: [], assessments: [], recommendations: [], roadmaps: [], projects: [] },
            serviceLibrary: [],
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const liveMetrics = useMemo(() => (workspaceData ? calculateTruevanceMetrics(workspaceData.store) : null), [workspaceData]);
  const serviceLibrary = workspaceData?.serviceLibrary || [];

  const navigationCards = useMemo(
    () =>
      truevanceWorkspaceLinks.map((item) => {
        const active = location.pathname === item.to;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={active ? "page" : undefined}
            className={`focus-ring grid gap-2 rounded-lg border p-4 transition hover:-translate-y-0.5 ${
              active
                ? "border-[#FF6A2B]/55 bg-[#FF6A2B]/12 text-[#fff9ef]"
                : "border-[#FF6A2B]/20 bg-white/5 text-[#fff9ef] hover:border-[#FF6A2B]/55 hover:bg-white/10"
            }`}
          >
            <span className="flex items-center justify-between gap-3">
              <span className="font-black">{item.label}</span>
              <ArrowRight className="size-4 shrink-0 text-[#FF6A2B]" aria-hidden="true" />
            </span>
            <span className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{item.description}</span>
          </Link>
        );
      }),
    [location.pathname],
  );

  const operationalCards = useMemo(
    () =>
      truevanceOperationalLinks.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="focus-ring grid gap-2 rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4 transition hover:-translate-y-0.5 hover:border-[#FF6A2B]/55 hover:bg-white/10"
          title={`${item.label}: ${item.description}`}
        >
          <span className="flex items-center justify-between gap-3">
            <span className="font-black text-[#fff9ef]">{item.label}</span>
            <ArrowRight className="size-4 shrink-0 text-[#FF6A2B]" aria-hidden="true" />
          </span>
          <span className="text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">{item.description}</span>
        </Link>
      )),
    [],
  );

  return (
    <main className="truevance-shell command-center-shell min-h-screen text-[#fff9ef]">
      <TruevanceBrandHeader />

      <section className="truevance-hero relative isolate overflow-hidden border-b border-[#D94A16]/18 bg-[#050505] px-4 py-12 text-[#fff9ef] sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,224,208,.2),transparent_34%),radial-gradient(circle_at_top_right,rgba(214,178,74,.18),transparent_30%),linear-gradient(120deg,rgba(7,26,47,.96),rgba(10,37,64,.92))]" />
        <div className="relative mx-auto grid max-w-7xl gap-5">
          <div className="inline-flex w-fit items-center gap-2 rounded-full border border-[rgba(34,224,208,.32)] px-3 py-2 text-xs font-black uppercase tracking-[0.2em] text-[#22E0D0]">
            <Sparkles className="size-4" aria-hidden="true" />
            {details.eyebrow}
          </div>
          <h1 className="max-w-4xl font-display text-5xl font-bold leading-tight sm:text-6xl">{details.title}</h1>
          <p className="max-w-3xl text-base font-semibold leading-8 text-[#E8EEF4]/82">{details.description}</p>
        </div>
      </section>

      <section className="px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8">
          {liveMetrics ? (
            <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <article className="command-center-card rounded-lg p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Open Opportunities</p>
                <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{liveMetrics.openOpportunities}</p>
                <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">Active consulting opportunities in the pipeline.</p>
              </article>
              <article className="command-center-card rounded-lg p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Active Assessments</p>
                <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{liveMetrics.activeAssessments}</p>
                <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">Assessments currently in progress.</p>
              </article>
              <article className="command-center-card rounded-lg p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Consulting Revenue</p>
                <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{formatCurrency(liveMetrics.consultingRevenue)}</p>
                <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">Revenue recorded across consulting project workspaces.</p>
              </article>
              <article className="command-center-card rounded-lg p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">Project Completion</p>
                <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{formatPercent(liveMetrics.projectCompletionRate)}</p>
                <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">Average completion across active project records.</p>
              </article>
            </section>
          ) : null}

          <section className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
            {engagementMetrics.map((metric) => (
              <article key={metric.label} className="command-center-card rounded-lg p-5">
                <p className="text-xs font-black uppercase tracking-[0.18em] text-[#D94A16]">{metric.label}</p>
                <p className="mt-2 font-display text-3xl font-bold text-[#050505]">{metric.value}</p>
                <p className="mt-3 text-sm font-semibold leading-6 text-[#5c5246]">{metric.note}</p>
              </article>
            ))}
          </section>

          <section className="command-center-card rounded-lg p-5 sm:p-6" id="navigation">
            <SectionHeading
              icon={Workflow}
              title="Truevance Connect Business Center Workspaces"
              subtitle="Switch between the consulting pipeline, persistent operational records, service categories, and reusable platform architecture."
            />
            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">{navigationCards}</div>
          </section>

          <section className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading icon={Target} title="Operational Workspaces" subtitle="Open the persistent consulting records that power the Truevance operating layer." />
            <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">{operationalCards}</div>
          </section>

          <section className="grid gap-6 xl:grid-cols-[1.15fr_.85fr]">
            <article id="consulting-pipeline" className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading
                icon={BriefcaseBusiness}
                title="Consulting Pipeline"
                subtitle="Move from discovery consultation to ongoing support with a clear stage-by-stage operating model."
              />
              <div className="mt-5 grid gap-3">
                {consultingPipelineStages.map((stage, index) => (
                  <div key={stage.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                          {String(index + 1).padStart(2, "0")}
                        </p>
                        <h3 className="mt-1 font-display text-2xl font-bold text-[#fff9ef]">{stage.label}</h3>
                      </div>
                      <span className="rounded-full border border-[#FF6A2B]/24 px-3 py-1 text-xs font-black uppercase tracking-[0.14em] text-[#FF6A2B]">
                        {stage.signal}
                      </span>
                    </div>
                    <p className="mt-3 text-sm font-semibold leading-7 text-[#fff9ef]/74">{stage.summary}</p>
                    <p className="mt-2 text-sm font-bold text-[#fff9ef]">Next: {stage.next}</p>
                  </div>
                ))}
              </div>
            </article>

            <article id="assessment-framework" className="command-center-card rounded-lg p-5 sm:p-6">
              <SectionHeading
                icon={SearchCheck}
                title="Assessment Framework"
                subtitle="Document the business story before solution design begins."
              />
              <div className="mt-5 grid gap-3">
                {assessmentFramework.map((block) => (
                  <div key={block.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                    <h3 className="font-display text-2xl font-bold text-[#fff9ef]">{block.label}</h3>
                    <ul className="mt-3 grid gap-2 text-sm font-semibold leading-6 text-[#fff9ef]/74">
                      {block.items.map((item) => (
                        <li key={item} className="rounded-md border border-[#FF6A2B]/14 bg-black/15 px-3 py-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </article>
          </section>

          <section id="client-engagement-dashboard" className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              icon={CircleGauge}
              title="Client Engagement Dashboard"
              subtitle="Spot active consulting clients, assessments, implementations, and opportunities at a glance."
            />
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {engagementMetrics.map((metric) => (
                <div key={`${metric.label}-detail`} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#D94A16]">{metric.label}</p>
                  <p className="mt-2 font-display text-3xl font-bold text-[#fff9ef]">{metric.value}</p>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/74">{metric.note}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="service-categories" className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              icon={Layers3}
              title="Service Categories"
              subtitle="Organize the consulting offer into flexible categories that can be reused for future industries and templates."
            />
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {serviceLibrary.length
                ? serviceLibrary.map((service) => (
                    <article key={service.id} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                      <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{service.serviceCategory}</p>
                      <h3 className="mt-2 font-display text-2xl font-bold text-[#fff9ef]">{service.serviceName}</h3>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/74">{service.description}</p>
                    </article>
                  ))
                : serviceCategories.map((service) => (
                    <article key={service.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                      <h3 className="font-display text-2xl font-bold text-[#fff9ef]">{service.label}</h3>
                      <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/74">{service.description}</p>
                    </article>
                  ))}
            </div>
          </section>

          <section id="platform-architecture" className="command-center-card rounded-lg p-5 sm:p-6">
            <SectionHeading
              icon={PencilRuler}
              title="Reusable Platform Architecture"
              subtitle="The following building blocks can later be promoted into ClientFlow Studio as white-label core modules."
            />
            <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {reusablePatterns.map((pattern) => (
                <article key={pattern.label} className="rounded-lg border border-[#FF6A2B]/20 bg-white/5 p-4">
                  <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">{pattern.destination}</p>
                  <h3 className="mt-2 font-display text-2xl font-bold text-[#fff9ef]">{pattern.label}</h3>
                  <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/74">{pattern.description}</p>
                </article>
              ))}
            </div>
            <div className="mt-6 rounded-lg border border-[#FF6A2B]/20 bg-[#FF6A2B]/10 p-4">
              <p className="text-xs font-black uppercase tracking-[0.16em] text-[#FF6A2B]">ClientFlow Studio Preparation</p>
              <p className="mt-2 text-sm font-semibold leading-7 text-[#fff9ef]/76">
                These patterns can be lifted into a shared core engine, a white-label workspace kit, industry templates,
                and reusable business operating modules when ClientFlow Studio is ready.
              </p>
            </div>
          </section>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => navigate("/admin/truevance-business-center")}
              className="focus-ring inline-flex min-h-12 items-center justify-center rounded-lg bg-[#FF6A2B] px-5 text-sm font-extrabold text-[#050505] transition hover:bg-[#FF6A2B]"
            >
              Back to Truevance Business Center
            </button>
            <Link
              to="/admin/executive-dashboard"
              className="focus-ring inline-flex min-h-12 items-center justify-center rounded-lg border border-[rgba(34,224,208,.32)] px-5 text-sm font-extrabold text-[#22E0D0] transition hover:bg-white/10"
            >
              Open Executive Dashboard
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

function SectionHeading({ icon: Icon, title, subtitle }: { icon: typeof Sparkles; title: string; subtitle: string }) {
  return (
    <div className="border-b border-[#FF6A2B]/14 pb-4">
      <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-[#22E0D0]">
        <Icon className="size-4 text-[#17D4C4]" aria-hidden="true" />
        {TRUEVANCE_BRAND_NAME}
      </p>
      <h2 className="mt-1 font-display text-3xl font-bold text-[#fff9ef]">{title}</h2>
      <p className="mt-3 max-w-4xl text-sm font-semibold leading-7 text-[#E8EEF4]/72">{subtitle}</p>
    </div>
  );
}
